import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from '../../utils/axios';
import { getChecklistSuggestions, downloadFilesZip } from '../../utils/urls';
import '../../pages/pendingClosingQueue/pendingClosingQueue.css';
import downloadIcon from '../../Images/download-icon.png';

function PdfThumbnail({ url }) {
    return (
        <div className="pcq-doc-pdf-frame-wrap">
            <iframe
                src={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
                title="PDF preview"
                className="pcq-doc-pdf-frame"
                scrolling="no"
            />
        </div>
    );
}

function getInitials(name) {
    if (!name) return '?';
    return name.trim().split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
}

const resolveFileUrl = (url) => url?.startsWith('/') ? `https://media.bcrumbs.com${url}` : url;

export default function DocsModal({
    visible, onHide, card,
    allFilesForModal, allItems, fmtFileSize,
    renamingFileId, setRenamingFileId, renameValue, setRenameValue, handleRenameFile,
    handleRefileDoc, handleAssignPendingDoc, handleAssignIndependentToHeading, localHeadings,
    handleRenameToSuggestion,
    setPreviewFile, setPreviewVisible,
    docViewMode, setDocViewMode,
    handleModalUpload, handleDragDropUpload, modalUploading,
    handleDeleteDoc,
    hasMoreDocs, loadingMoreDocs, onLoadMoreDocs, docsLoaded, docsTotal,
}) {
    const fileInputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [dragOverModal, setDragOverModal] = useState(false);
    const dragCountRef = useRef(0);
    const [itemSearch, setItemSearch] = useState({ key: null, query: '' });
    const [suggestions, setSuggestions] = useState([]);
    const [ddPos, setDdPos] = useState(null);
    const inputRefs = useRef({});
    const [clPickerKey, setClPickerKey] = useState(null);
    const [clPickerPos, setClPickerPos] = useState(null);
    const [sortDir, setSortDir] = useState('desc');
    const [zipMode, setZipMode] = useState(false);
    const [zipSelected, setZipSelected] = useState(new Set());
    const [zipLoading, setZipLoading] = useState(false);
    const [zipError, setZipError] = useState('');
    const bodyRef = useRef(null);
    const loadMoreRef = useRef(null);

    // Fetch the next page when the bottom of the list scrolls into view. The
    // observer is recreated after each load, so if the sentinel is still
    // visible (short list) it fires again straight away.
    useEffect(() => {
        const root = bodyRef.current, sentinel = loadMoreRef.current;
        if (!visible || !hasMoreDocs || loadingMoreDocs || !root || !sentinel) return;
        const observer = new IntersectionObserver(
            entries => { if (entries[0].isIntersecting) onLoadMoreDocs(); },
            { root, rootMargin: '200px' }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [visible, hasMoreDocs, loadingMoreDocs, docsLoaded, onLoadMoreDocs]);

    useEffect(() => {
        if (!clPickerKey) return;
        const close = (e) => {
            if (e.target.closest('.pcq-cl-popup')) return;
            if (e.target.closest('.pcq-dv-cl-icon-btn')) return;
            if (e.target.closest('.pcq-dv-item-dd')) return;
            setClPickerKey(null); setClPickerPos(null);
        };
        document.addEventListener('mousedown', close, true);
        return () => document.removeEventListener('mousedown', close, true);
    }, [clPickerKey]);

    useEffect(() => {
        if (!itemSearch.key) { setSuggestions([]); return; }
        const t = setTimeout(async () => {
            try {
                const q = itemSearch.query ? `&search=${encodeURIComponent(itemSearch.query)}` : '';
                const res = await axios.get(`${getChecklistSuggestions}?limit=10${q}`);
                if (res.status === 200) setSuggestions(res.data.results || []);
            } catch {}
        }, 300);
        return () => clearTimeout(t);
    }, [itemSearch.key, itemSearch.query]);

    const getItemName = id => allItems.find(it => it.id === Number(id))?.item || '';

    const openItemSearch = (key, currentItemId) => {
        const el = inputRefs.current[key];
        if (el) {
            const r = el.getBoundingClientRect();
            setDdPos({ top: r.bottom + 4, left: r.left, width: r.width });
        }
        setItemSearch({ key, query: getItemName(currentItemId) });
    };

    const closeItemSearch = (query, currentItemId, onCreateByName) =>
        setTimeout(() => {
            const q = (query || '').trim();
            if (q && onCreateByName) {
                const currentName = getItemName(currentItemId);
                const matchesExisting = allItems.some(
                    it => (it.item || '').toLowerCase() === q.toLowerCase()
                );
                if (!matchesExisting && q !== currentName) {
                    onCreateByName(q);
                }
            }
            setItemSearch({ key: null, query: '' });
            setDdPos(null);
        }, 150);

    const renderItemInput = (key, currentItemId, isUnfiled, onSelect, onCreateByName) => {
        const isOpen     = itemSearch.key === key;
        const inputValue = isOpen ? itemSearch.query : getItemName(currentItemId);

        const filtered = allItems.filter(it =>
            (it.item || '').toLowerCase().includes((itemSearch.query || '').toLowerCase())
        );
        const filteredNames = new Set(filtered.map(it => (it.item || '').toLowerCase()));

        // API suggestions not already shown in filtered — map to allItem if a match exists
        const apiExtra = suggestions
            .filter(s => !filteredNames.has((s.name || '').toLowerCase()))
            .map(s => {
                const match = allItems.find(
                    it => (it.item || '').toLowerCase() === (s.name || '').toLowerCase()
                );
                return { name: s.name, match };
            });

        const showDropdown = isOpen && (filtered.length > 0 || apiExtra.length > 0);

        return (
            <div className="pcq-dv-item-wrap">
                <input
                    ref={el => { inputRefs.current[key] = el; }}
                    className={`pcq-dv-item-input${isUnfiled ? ' unfiled' : ''}`}
                    value={inputValue}
                    placeholder={isUnfiled ? '⚠ Unfiled — search to assign…' : 'Search checklist items…'}
                    onFocus={() => openItemSearch(key, currentItemId)}
                    onChange={e => {
                        const el = inputRefs.current[key];
                        if (el) { const r = el.getBoundingClientRect(); setDdPos({ top: r.bottom + 4, left: r.left, width: r.width }); }
                        setItemSearch({ key, query: e.target.value });
                    }}
                    onBlur={() => closeItemSearch(itemSearch.query, currentItemId, onCreateByName)}
                    readOnly={!isOpen}
                />
                {showDropdown && (
                    <div className="pcq-dv-item-dd" style={ddPos ? { background:"#292929", position: 'fixed', top: ddPos.top, left: ddPos.left, width: ddPos.width, zIndex: 9999 } : {}}>
                        {filtered.map(it => (
                            <div key={it.id} className="pcq-dv-item-opt"
                                onMouseDown={e => {
                                    e.preventDefault();
                                    onSelect(it.id);
                                    setItemSearch({ key: null, query: '' });
                                }}>
                                {it.item || '(unnamed)'}
                            </div>
                        ))}
                        {apiExtra.length > 0 && filtered.length > 0 && (
                            <div className="pcq-dv-item-dd-sep" />
                        )}
                        {apiExtra.map((s, i) => {
                            const actionable = s.match || !!onCreateByName;
                            return (
                                <div key={`sug-${i}`}
                                    className={`pcq-dv-item-opt pcq-dv-item-sug${actionable ? '' : ' disabled'}`}
                                    onMouseDown={e => {
                                        e.preventDefault();
                                        if (s.match) {
                                            onSelect(s.match.id);
                                        } else if (onCreateByName) {
                                            onCreateByName(s.name);
                                        }
                                        setItemSearch({ key: null, query: '' });
                                    }}>
                                    {s.name}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const toggleZipDoc = (id) => {
        setZipSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleZipDownload = async () => {
        if (zipSelected.size === 0) return;
        setZipLoading(true);
        setZipError('');
        try {
            const res = await axios.post(
                downloadFilesZip,
                { files: [...zipSelected].map(id => ({ app_doc_id: id })) },
                { responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.setAttribute('download', `${(card?.full_name || 'documents').replace(/[^a-zA-Z0-9]/g, '_')}_files.zip`);
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            setZipMode(false);
            setZipSelected(new Set());
        } catch {
            setZipError('Download failed. Please try again.');
        } finally {
            setZipLoading(false);
        }
    };

    if (!visible) return null;

    const parseUploadedAt = str => {
        if (!str) return 0;
        const d = new Date(str);
        return isNaN(d.getTime()) ? 0 : d.getTime();
    };

    const fileId    = (card.customFields || []).find(f => f.name === 'Loan File ID')?.value;
    const totalSize = allFilesForModal.reduce((n, f) => n + (f.file_size || 0), 0);
    const iconMap   = { pdf: '📄', jpg: '🖼', jpeg: '🖼', png: '🖼', doc: '📝', docx: '📝', xlsx: '📊', xls: '📊' };
    const getIcon   = name => iconMap[(name || '').split('.').pop().toLowerCase()] || '📄';

    const sortedFiles = [...allFilesForModal].sort((a, b) => {
        const tA = parseUploadedAt(a.uploaded_at_iso);
        const tB = parseUploadedAt(b.uploaded_at_iso);
        return sortDir === 'desc' ? tB - tA : tA - tB;
    });

    const allSelectableIds = sortedFiles.filter(f => f.app_doc_id).map(f => f.app_doc_id);
    const allZipSelected   = allSelectableIds.length > 0 && allSelectableIds.every(id => zipSelected.has(id));
    const toggleSelectAll  = () => {
        if (allZipSelected) setZipSelected(new Set());
        else setZipSelected(new Set(allSelectableIds));
    };
    const enterZipMode = () => {
        setZipSelected(new Set(allSelectableIds));
        setZipMode(true);
    };

    // Group filed docs by itemId; unfiled get their own rows
    const itemOrder = [];
    const byItemId  = {};
    const unfiledList = [];
    sortedFiles.forEach(f => {
        if (f.isPending || f.isIndependent || !f.itemId) {
            unfiledList.push(f);
        } else {
            if (!byItemId[f.itemId]) { byItemId[f.itemId] = []; itemOrder.push(f.itemId); }
            byItemId[f.itemId].push(f);
        }
    });

    const renderChecklistBtn = (f, docKey, onSelect, onCreateByName) => {
        const isOpen    = clPickerKey === docKey;
        const isUnfiled = f.isPending || (!f.isIndependent && !f.itemId);
        return (
            <>
                <button
                    className="pcq-dv-cl-icon-btn"
                    title="Assign to checklist"
                    onClick={e => {
                        e.stopPropagation();
                        if (isOpen) { setClPickerKey(null); setClPickerPos(null); return; }
                        const r = e.currentTarget.getBoundingClientRect();
                        setClPickerPos({ top: r.bottom + 6, left: r.left });
                        setClPickerKey(docKey);
                    }}
                >
                    📋
                </button>
                {isOpen && clPickerPos && createPortal(
                    <div
                        className="pcq-cl-popup"
                        style={{
                            position: 'fixed',
                            top: clPickerPos.top,
                            left: clPickerPos.left,
                            zIndex: 99999,
                            minWidth: 280,
                            padding: 12,
                            backgroundColor: '#1a1a1d',
                            color: '#f0f0f2',
                            border: '1px solid rgba(255,255,255,0.18)',
                            borderRadius: 10,
                            boxShadow: '0 12px 40px rgba(0,0,0,0.85)',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {f.isIndependent ? (
                            (localHeadings || []).length === 0
                                ? <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, padding: '4px 2px' }}>No checklist headings yet</div>
                                : (localHeadings || []).map(h => (
                                    <button key={h.id} className="pcq-ind-heading-opt"
                                        onMouseDown={e => {
                                            e.preventDefault();
                                            handleAssignIndependentToHeading(f, h.id);
                                            setClPickerKey(null); setClPickerPos(null);
                                        }}>
                                        {h.name}
                                    </button>
                                ))
                        ) : renderItemInput(docKey, f.itemId, isUnfiled, id => {
                            if (onSelect) onSelect(id);
                            setClPickerKey(null); setClPickerPos(null);
                        }, onCreateByName)}
                    </div>,
                    document.body
                )}
            </>
        );
    };

    const renderFileRow = (f, docKey, extraClass = '') => {
        const isRenaming = !f.isPending && renamingFileId === docKey;
        const isUnfiled  = f.isPending || (!f.isIndependent && !f.itemId);
        const ext        = (f.file_name || '').split('.').pop().toLowerCase();
        const isImage    = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
        return (
            <div key={docKey} className={`pcq-dv-trow${extraClass ? ` ${extraClass}` : ''}${zipMode && f.app_doc_id && zipSelected.has(f.app_doc_id) ? ' zip-selected' : ''}`}>
                <span className="pcq-dv-name">
                    {zipMode && f.app_doc_id && (
                        <input type="checkbox" className="pcq-zip-cb"
                            checked={zipSelected.has(f.app_doc_id)}
                            onChange={() => toggleZipDoc(f.app_doc_id)}
                            onClick={e => e.stopPropagation()} />
                    )}
                    {isImage && f.file_url ? (
                        <img src={resolveFileUrl(f.file_url)} alt="" className="pcq-list-thumb"
                            onClick={() => { setPreviewFile({ ...f, file_url: resolveFileUrl(f.file_url) }); setPreviewVisible(true); }} />
                    ) : (
                        <span className="pcq-dv-ic"
                            onClick={() => { setPreviewFile({ ...f, file_url: resolveFileUrl(f.file_url) }); setPreviewVisible(true); }}>
                            {getIcon(f.file_name)}
                        </span>
                    )}
                    {isRenaming ? (
                        <input
                            className="pcq-dv-fn-edit"
                            value={renameValue}
                            autoFocus
                            onChange={e => setRenameValue(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') handleRenameFile(f, renameValue);
                                if (e.key === 'Escape') setRenamingFileId(null);
                            }}
                            onBlur={() => handleRenameFile(f, renameValue)}
                            onClick={e => e.stopPropagation()}
                        />
                    ) : (
                        <>
                            <span
                                className="pcq-dv-fn"
                                onClick={() => { setPreviewFile({ ...f, file_url: resolveFileUrl(f.file_url) }); setPreviewVisible(true); }}
                                title="Click to preview"
                            >{f.file_name}</span>
                            {!f.isPending && (
                                <button className="pcq-dv-fn-edit-btn"
                                    onClick={e => { e.stopPropagation(); setRenamingFileId(docKey); setRenameValue(f.file_name); }}
                                    title="Rename file">✏️</button>
                            )}
                        </>
                    )}
                </span>
                <span className="pcq-dv-size">{fmtFileSize(f.file_size)}</span>
                <span className="pcq-dv-by">
                    {f.uploaded_by
                        ? ((f.uploaded_by.profile_pic_url || f.uploaded_by.image)
                            ? <img src={f.uploaded_by.profile_pic_url || f.uploaded_by.image} alt="" className="pcq-dv-av-img" />
                            : <div className="pcq-dv-av">{getInitials(f.uploaded_by.name)}</div>)
                        : null}
                    <span>{f.uploaded_by?.name?.split(' ')[0] || '—'}</span>
                </span>
                <span className="pcq-dv-when">{f.uploaded_at || '—'}</span>
                <span className="pcq-dv-actions">
                    {renderChecklistBtn(f, docKey,
                        newId => { if (f.isPending) handleAssignPendingDoc(f, newId); else handleRefileDoc(f, newId); },
                        isUnfiled ? undefined : name => handleRenameToSuggestion(f, name)
                    )}
                    {f.file_url && (
                        <a className="pcq-dv-dl-link" href={resolveFileUrl(f.file_url)}
                            download={f.file_name} target="_blank" rel="noreferrer"
                            onClick={e => e.stopPropagation()} title="Download">
                            <img src={downloadIcon} alt="download" style={{ width: '18px', height: '18px', verticalAlign: 'middle' }} />
                        </a>
                    )}
                    <button className="pcq-dv-del-btn" title="Delete document"
                        onClick={e => { e.stopPropagation(); handleDeleteDoc(f); }}>
                        🗑
                    </button>
                </span>
            </div>
        );
    };

    const renderGroupedRows = (itemId, files) => {
        return (
            <div key={`grp-${itemId}`} className="pcq-dv-group">
                {files.map((f, fi) => {
                    const isFirst    = fi === 0;
                    const isLast     = fi === files.length - 1;
                    const docKey     = f.app_doc_id || f.id || `${itemId}-${fi}`;
                    const isRenaming = renamingFileId === docKey;
                    const ext        = (f.file_name || '').split('.').pop().toLowerCase();
                    const isImage    = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
                    return (
                        <div key={docKey} className={`pcq-dv-trow pcq-dv-trow-grouped${isLast ? '' : ' pcq-dv-trow-grouped-mid'}${zipMode && f.app_doc_id && zipSelected.has(f.app_doc_id) ? ' zip-selected' : ''}`}>
                            <span className="pcq-dv-name">
                                {zipMode && f.app_doc_id && (
                                    <input type="checkbox" className="pcq-zip-cb"
                                        checked={zipSelected.has(f.app_doc_id)}
                                        onChange={() => toggleZipDoc(f.app_doc_id)}
                                        onClick={e => e.stopPropagation()} />
                                )}
                                {isImage && f.file_url ? (
                                    <img src={resolveFileUrl(f.file_url)} alt="" className="pcq-list-thumb"
                                        onClick={() => { setPreviewFile({ ...f, file_url: resolveFileUrl(f.file_url) }); setPreviewVisible(true); }} />
                                ) : (
                                    <span className="pcq-dv-ic"
                                        onClick={() => { setPreviewFile({ ...f, file_url: resolveFileUrl(f.file_url) }); setPreviewVisible(true); }}>
                                        {getIcon(f.file_name)}
                                    </span>
                                )}
                                {isRenaming ? (
                                    <input
                                        className="pcq-dv-fn-edit"
                                        value={renameValue}
                                        autoFocus
                                        onChange={e => setRenameValue(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') handleRenameFile(f, renameValue);
                                            if (e.key === 'Escape') setRenamingFileId(null);
                                        }}
                                        onBlur={() => handleRenameFile(f, renameValue)}
                                        onClick={e => e.stopPropagation()}
                                    />
                                ) : (
                                    <>
                                        <span
                                            className="pcq-dv-fn"
                                            onClick={() => { setPreviewFile({ ...f, file_url: resolveFileUrl(f.file_url) }); setPreviewVisible(true); }}
                                            title="Click to preview"
                                        >{f.file_name}</span>
                                        <button className="pcq-dv-fn-edit-btn"
                                            onClick={e => { e.stopPropagation(); setRenamingFileId(docKey); setRenameValue(f.file_name); }}
                                            title="Rename file">✏️</button>
                                    </>
                                )}
                            </span>
                            <span className="pcq-dv-size">{fmtFileSize(f.file_size)}</span>
                            <span className="pcq-dv-by">
                                {f.uploaded_by
                                    ? ((f.uploaded_by.profile_pic_url || f.uploaded_by.image)
                                        ? <img src={f.uploaded_by.profile_pic_url || f.uploaded_by.image} alt="" className="pcq-dv-av-img" />
                                        : <div className="pcq-dv-av">{getInitials(f.uploaded_by.name)}</div>)
                                    : null}
                                <span>{f.uploaded_by?.name?.split(' ')[0] || '—'}</span>
                            </span>
                            <span className="pcq-dv-when">{f.uploaded_at || '—'}</span>
                            <span className="pcq-dv-actions">
                                {isFirst && renderChecklistBtn(f, `group-${itemId}`,
                                    newId => { files.forEach(gf => handleRefileDoc(gf, newId)); },
                                    name => handleRenameToSuggestion(files[0], name)
                                )}
                                {f.file_url && (
                                    <a className="pcq-dv-dl-link" href={resolveFileUrl(f.file_url)}
                                        download={f.file_name} target="_blank" rel="noreferrer"
                                        onClick={e => e.stopPropagation()} title="Download">
                                        <img src={downloadIcon} alt="download" style={{ width: '18px', height: '18px', verticalAlign: 'middle' }} />
                                    </a>
                                )}
                                <button className="pcq-dv-del-btn" title="Delete document"
                                    onClick={e => { e.stopPropagation(); handleDeleteDoc(f); }}>
                                    🗑
                                </button>
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    };

    return createPortal(
        <div className="pcq-modal-backdrop" onClick={onHide} style={{ zIndex: 5300 }}>
            <div className="pcq-modal" onClick={e => e.stopPropagation()}
            onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
            onDragEnter={e => { e.preventDefault(); dragCountRef.current++; setDragOverModal(true); }}
            onDragLeave={() => { dragCountRef.current--; if (dragCountRef.current === 0) setDragOverModal(false); }}
            onDrop={e => {
                e.preventDefault(); e.stopPropagation();
                dragCountRef.current = 0; setDragOverModal(false);
                const fs = [...e.dataTransfer.files];
                if (fs.length) (handleDragDropUpload || handleModalUpload)(fs);
            }}
        >

                {/* Full-modal drag overlay */}
                {dragOverModal && (
                    <div className="pcq-modal-drop-overlay">
                        <div className="pcq-modal-drop-content">
                            <span className="pcq-modal-drop-arrow">⬆</span>
                            <span className="pcq-modal-drop-title">Drop to upload</span>
                            <span className="pcq-modal-drop-sub">Files land as Unfiled — label them after</span>
                        </div>
                    </div>
                )}

                {/* ── Close button — pinned top-right ── */}
                <button className="pcq-modal-x" onClick={onHide} title="Close">✕</button>

                {/* ── Header ── */}
                <div className="pcq-modal-hd">
                    <div className="pcq-modal-hd-left">
                        <div className="pcq-modal-name">{card.full_name}</div>
                        <div className="pcq-modal-sub">
                            {allFilesForModal.length} file{allFilesForModal.length !== 1 ? 's' : ''}
                            {' · '}{fmtFileSize(totalSize)}
                            {fileId && ` · ${fileId}`}
                        </div>
                    </div>

                    <input type="file" multiple ref={fileInputRef} style={{ display: 'none' }}
                        onChange={e => { (handleDragDropUpload || handleModalUpload)([...e.target.files]); e.target.value = ''; }} />

                    <div
                        className={`pcq-modal-dz${dragging ? ' drag' : ''}${modalUploading ? ' uploading' : ''}`}
                        onDragOver={e => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={e => {
                            e.preventDefault(); e.stopPropagation(); setDragging(false);
                            const fs = [...e.dataTransfer.files];
                            if (fs.length) (handleDragDropUpload || handleModalUpload)(fs);
                        }}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <span className="pcq-modal-dz-ic">📁</span>
                        <span>{modalUploading ? 'Uploading…' : <><b>Drag &amp; drop</b> to upload</>}</span>
                    </div>

                    <div className="pcq-modal-hd-right">
                        <div className="pcq-dv-viewtoggle">
                            <button className={`pcq-dv-vbtn${docViewMode === 'list' ? ' on' : ''}`} onClick={() => setDocViewMode('list')}>☰ List</button>
                            <button className={`pcq-dv-vbtn${docViewMode === 'tile' ? ' on' : ''}`} onClick={() => setDocViewMode('tile')}>⊞ Tiles</button>
                        </div>
                        <button className="pcq-icon-btn accent" onClick={enterZipMode} disabled={allFilesForModal.length === 0 || zipMode}>
                            ⬇ Download Zip
                        </button>
                    </div>
                </div>

                {/* ── Body ── */}
                <div className="pcq-modal-body" ref={bodyRef}>
                    {docViewMode === 'list' ? (
                        <div className="pcq-dv-list-wrap">
                            {zipMode && (
                                <div className="pcq-zip-bar">
                                    <label className="pcq-zip-selall">
                                        <input type="checkbox" checked={allZipSelected} onChange={toggleSelectAll} onClick={e => e.stopPropagation()} />
                                        Select all ({allSelectableIds.length})
                                    </label>
                                    <span className="pcq-zip-count">{zipSelected.size} of {allSelectableIds.length} selected</span>
                                    <div className="pcq-zip-actions">
                                        {zipError && <span className="pcq-zip-err">{zipError}</span>}
                                        <button className="pcq-zip-dl-btn" onClick={handleZipDownload} disabled={zipSelected.size === 0 || zipLoading}>
                                            {zipLoading ? '⏳ Zipping…' : `⬇ Download (${zipSelected.size})`}
                                        </button>
                                        <button className="pcq-zip-cancel-btn" onClick={() => { setZipMode(false); setZipSelected(new Set()); setZipError(''); }}>
                                            ✕ Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                            {allFilesForModal.length === 0 && (
                                <p className="pcq-cl-empty" style={{ textAlign: 'center', padding: '40px 0' }}>No documents uploaded yet.</p>
                            )}
                            {allFilesForModal.length > 0 && (
                                <>
                                    <div className="pcq-dv-thead">
                                        <span>Document</span><span>Size</span><span>Uploaded by</span>
                                        <span className="pcq-dv-sort-hd" onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}>
                                            When {sortDir === 'desc' ? '↓' : '↑'}
                                        </span>
                                        <span></span>
                                    </div>
                                    {/* Filed groups — multiple docs share grouped rows */}
                                    {itemOrder.map(itemId => {
                                        const files = byItemId[itemId];
                                        if (files.length === 1) {
                                            const f      = files[0];
                                            const docKey = f.app_doc_id || f.id || `${itemId}-0`;
                                            return renderFileRow(f, docKey);
                                        }
                                        return renderGroupedRows(itemId, files);
                                    })}
                                    {/* Unfiled docs — each gets its own row */}
                                    {unfiledList.map((f, i) => {
                                        const docKey = f.app_doc_id || f.id || `unfiled-${i}`;
                                        return renderFileRow(f, docKey);
                                    })}
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="pcq-dv-list-wrap">
                            {zipMode && (
                                <div className="pcq-zip-bar">
                                    <label className="pcq-zip-selall">
                                        <input type="checkbox" checked={allZipSelected} onChange={toggleSelectAll} onClick={e => e.stopPropagation()} />
                                        Select all ({allSelectableIds.length})
                                    </label>
                                    <span className="pcq-zip-count">{zipSelected.size} of {allSelectableIds.length} selected</span>
                                    <div className="pcq-zip-actions">
                                        {zipError && <span className="pcq-zip-err">{zipError}</span>}
                                        <button className="pcq-zip-dl-btn" onClick={handleZipDownload} disabled={zipSelected.size === 0 || zipLoading}>
                                            {zipLoading ? '⏳ Zipping…' : `⬇ Download (${zipSelected.size})`}
                                        </button>
                                        <button className="pcq-zip-cancel-btn" onClick={() => { setZipMode(false); setZipSelected(new Set()); setZipError(''); }}>
                                            ✕ Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        <div className="pcq-docs-grid">
                            {sortedFiles.length === 0 && <p className="pcq-cl-empty">No documents uploaded yet.</p>}
                            {sortedFiles.map((f, i) => {
                                const docKey = f.app_doc_id || f.id || `tile-${i}`;
                                const isRenaming = !f.isPending && renamingFileId === docKey;
                                const knownExts = ['pdf','jpg','jpeg','png','gif','webp','bmp','svg','doc','docx','xlsx','xls','ppt','pptx','txt','zip','rar'];
                                const extFromName = (f.file_name || '').split('.').pop().toLowerCase();
                                const extFromUrl = (resolveFileUrl(f.file_url) || '').split('?')[0].split('.').pop().toLowerCase();
                                const ext = knownExts.includes(extFromName) ? extFromName : (knownExts.includes(extFromUrl) ? extFromUrl : extFromName);
                                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
                                const isPdf = ext === 'pdf';
                                const resolvedUrl = resolveFileUrl(f.file_url);
                                const extColorMap = { pdf: '#e53e3e', doc: '#3182ce', docx: '#3182ce', xlsx: '#38a169', xls: '#38a169', ppt: '#dd6b20', pptx: '#dd6b20', txt: '#718096', zip: '#805ad5', rar: '#805ad5' };
                                const extColor = extColorMap[ext] || '#4a5568';
                                const isTileSelected = zipMode && f.app_doc_id && zipSelected.has(f.app_doc_id);
                                return (
                                    <div key={docKey} className={`pcq-doc-tile${isTileSelected ? ' zip-selected' : ''}`}
                                        onClick={() => {
                                            if (zipMode && f.app_doc_id) { toggleZipDoc(f.app_doc_id); return; }
                                            if (!isRenaming) { setPreviewFile({ ...f, file_url: resolvedUrl }); setPreviewVisible(true); }
                                        }}>
                                        <div className="pcq-doc-thumb">
                                            {isImage ? (
                                                <img src={resolvedUrl} alt={f.file_name} className="pcq-doc-thumb-img" />
                                            ) : isPdf ? (
                                                <PdfThumbnail url={resolvedUrl} />
                                            ) : (
                                                <div className="pcq-doc-thumb-icon">
                                                    <svg width="64" height="78" viewBox="0 0 48 58" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M4 0h26l14 14v40a4 4 0 01-4 4H4a4 4 0 01-4-4V4a4 4 0 014-4z" fill="rgba(255,255,255,0.1)"/>
                                                        <path d="M30 0l14 14H30V0z" fill="rgba(255,255,255,0.2)"/>
                                                        <rect x="9" y="25" width="30" height="2.5" rx="1.25" fill="rgba(255,255,255,0.3)"/>
                                                        <rect x="9" y="33" width="24" height="2.5" rx="1.25" fill="rgba(255,255,255,0.3)"/>
                                                        <rect x="9" y="41" width="18" height="2.5" rx="1.25" fill="rgba(255,255,255,0.3)"/>
                                                    </svg>
                                                    <span className="pcq-doc-ext-badge" style={{ background: extColor }}>{ext.toUpperCase()}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="pcq-doc-meta">
                                            {isRenaming ? (
                                                <input
                                                    className="pcq-tile-rename-input"
                                                    value={renameValue}
                                                    autoFocus
                                                    onChange={e => setRenameValue(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') handleRenameFile(f, renameValue);
                                                        if (e.key === 'Escape') setRenamingFileId(null);
                                                    }}
                                                    onBlur={() => handleRenameFile(f, renameValue)}
                                                    onClick={e => e.stopPropagation()}
                                                />
                                            ) : (
                                                <div className="pcq-doc-name-row">
                                                    <div className="pcq-doc-name" title={f.file_name}>{f.file_name}</div>
                                                    {!f.isPending && (
                                                        <button className="pcq-doc-rename-btn"
                                                            onClick={e => { e.stopPropagation(); setRenamingFileId(docKey); setRenameValue(f.file_name); }}
                                                            title="Rename file">✏️</button>
                                                    )}
                                                </div>
                                            )}
                                            <div className="pcq-doc-sub">{f.itemLabel || (f.isPending ? 'Unfiled' : '—')}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        </div>
                    )}
                    {hasMoreDocs && (
                        <div className="pcq-docs-load-more-wrap" ref={loadMoreRef}>
                            {loadingMoreDocs && <i className="pi pi-spin pi-spinner pcq-docs-load-more-spin" />}
                            <span className="pcq-docs-load-more-count">
                                {loadingMoreDocs ? 'Loading more documents…' : `${docsLoaded} of ${docsTotal} loaded`}
                            </span>
                        </div>
                    )}
                </div>

                <div className="pcq-modal-footer">
                    <button className="pcq-docs-close-btn" onClick={onHide}>Close</button>
                </div>
            </div>
        </div>,
        document.body
    );
}
